import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthProvider';
import { useUI } from './UIContext';
import { normalizeKey, normalizeType, normalizeTag, normalizeGenre } from './utils/normalize'; // or wherever your normalizeKey() lives
import { sanitizeAbcBody } from './components/sanitizeAbcBody';
import myIcon from './assets/logo512.png'; // adjust path
import { downloadAllToLocal } from './utils/downloadAllToLocal';
import dexie from './offlineDB';
import { useUserPrefs } from './UserPrefsProvider';
import { getModeLabels } from './utils/modeLabels';
import { FaQuestionCircle } from 'react-icons/fa';
import AppSelect from './components/AppSelect';
import KeyInput from './components/KeyInput';
import ActionConfirmModal from './components/ActionConfirmModal';
import { useOnboarding } from './onboarding';


const baseGenres = ['Irish', 'Old-Time', 'Bluegrass', 'Klezmer'];
const baseTypes = ['Reel', 'Jig', 'Hornpipe', 'Polka', 'Waltz', 'Tune', 'Song'];
const baseTags = ['Awesome', 'Meh', 'To edit'];
const LEGACY_NOTES_CATEGORY = 'Notes';
const NOTES_DEFAULT_CATEGORY = 'General';
const DEFAULT_SEARCH_TOOLS = [
  { id: 'folkfriend', url: 'folkfriend.app', enabled: true, builtIn: true },
  { id: 'tunepal', url: 'tunepal.org', enabled: true, builtIn: true },
  { id: 'tunearch', url: 'tunearch.org', enabled: true, builtIn: true },
  { id: 'bluegrasslyrics', url: 'bluegrasslyrics.com', enabled: true, builtIn: true },
  { id: 'thesession', url: 'thesession.org', enabled: true, builtIn: true },
];

const normalizeToolUrl = (value = '') => value.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();
const makeCustomToolId = (url) => `custom:${normalizeToolUrl(url)}`;

const Settings = ({ onBack }) => {
  const navigate = useNavigate();
  const { uiSize, setUiSize, inputStyle } = useUI();
  const { user } = useAuth();
  const userPrefs = useUserPrefs();
  const hintsOn = userPrefs?.prefs?.showFieldHints || false;
  const liveAppMode = userPrefs?.prefs?.appMode;
  const [defaultGenre, setDefaultGenre] = useState('Irish');
  const [defaultType, setDefaultType] = useState('');
  const [defaultGenreByMode, setDefaultGenreByMode] = useState({ tunes: 'Irish', notes: NOTES_DEFAULT_CATEGORY });
  const [defaultTypeByMode, setDefaultTypeByMode] = useState({ tunes: '', notes: 'Note' });
  const [defaultKey, setDefaultKey] = useState('');
  const [defaultStatus, setDefaultStatus] = useState('Need to Learn');
  const [theme, setTheme] = useState('light');
  const [keySuggestions, setKeySuggestions] = useState([]);

  // Per-mode objects: { tunes: [...], notes: [...] }
  const [customGenres, setCustomGenres] = useState({
    tunes: [...new Set([...(getModeLabels('tunes').defaultGenres || []), ...baseGenres])],
    notes: [...(getModeLabels('notes').defaultGenres || [NOTES_DEFAULT_CATEGORY])]
  });
  const [customTypes, setCustomTypes] = useState({
    tunes: [...new Set([...(getModeLabels('tunes').defaultTypes || []), ...baseTypes])],
    notes: [...(getModeLabels('notes').defaultTypes || ['Note', 'Link', 'File', 'Audio', 'Idea', 'Reference', 'Task'])]
  });

  const [newGenreInput, setNewGenreInput] = useState('');
  const [newTypeInput, setNewTypeInput] = useState('');

  const [enableAutoSuggest, setEnableAutoSuggest] = useState('yes');

  const [usedGenres, setUsedGenres] = useState([]);
  const [usedTypes, setUsedTypes] = useState([]);
const [customTags, setCustomTags] = useState([]);
const [newTagInput, setNewTagInput] = useState('');
const [usedTags, setUsedTags] = useState([]);
const [usedTagsAnyMode, setUsedTagsAnyMode] = useState([]);


  const [showAbcInSets, setShowAbcInSets] = useState(true);
  const [showLyricsInSets, setShowLyricsInSets] = useState(true);
  const [showAbcInSearch, setShowAbcInSearch] = useState(true);
  const [showLyricsInSearch, setShowLyricsInSearch] = useState(false);
  const [showTagPreviewInSearch, setShowTagPreviewInSearch] = useState(true);
  const [showTuneCreatedDateInSearch, setShowTuneCreatedDateInSearch] = useState(true);
  const [showTagPreviewInSets, setShowTagPreviewInSets] = useState(true);
  const [showSetDateInSets, setShowSetDateInSets] = useState(true);
  const [showNotePreview, setShowNotePreview] = useState(true);
  const [showTagPreviewInSessions, setShowTagPreviewInSessions] = useState(true);
  const [showSessionDateInSessions, setShowSessionDateInSessions] = useState(true);
  const [showNoteCreatedDateInSearch, setShowNoteCreatedDateInSearch] = useState(true);

  const [searchTools, setSearchTools] = useState(DEFAULT_SEARCH_TOOLS);
  const [tunePageSearchToolsDisabled, setTunePageSearchToolsDisabled] = useState({});
  const [searchToolsSettingsTarget, setSearchToolsSettingsTarget] = useState('add');
  const [newSearchToolUrl, setNewSearchToolUrl] = useState('');
  const [searchToolsConfirm, setSearchToolsConfirm] = useState(null);
  const [addTuneFieldsByMode, setAddTuneFieldsByMode] = useState({ tunes: {}, notes: {} });
  const [appMode, setAppMode] = useState('tunes');
  const [allModeSettingsMode, setAllModeSettingsMode] = useState('tunes');
  const settingsMode = appMode === 'all' ? allModeSettingsMode : (appMode === 'notes' ? 'notes' : 'tunes');


  const [loading, setLoading] = useState(true);
  const [showSettingsLockPopup, setShowSettingsLockPopup] = useState(false);
  const [showModeToggleConfirm, setShowModeToggleConfirm] = useState(false);
  const [downloadAlertModal, setDownloadAlertModal] = useState(null); // { title, message }
  const { isSimpleMode, unlockFullMode, toggleComplexity } = useOnboarding();





const updatePreference = async (key, value) => {
  if (!user) return;
  await userPrefs.patchPrefs({ [key]: value });
  if (key === 'theme') localStorage.setItem('theme', value);
};

const resolveDefaultMode = (mode) => (mode === 'notes' ? 'notes' : 'tunes');
const normalizeNotesCategoryValue = (value) =>
  typeof value === 'string' && value.trim().toLowerCase() === LEGACY_NOTES_CATEGORY.toLowerCase()
    ? NOTES_DEFAULT_CATEGORY
    : value;
const normalizeNotesCategoryList = (values = []) => {
  const arr = Array.isArray(values) ? values : [];
  const mapped = arr.map(v => normalizeNotesCategoryValue(v));
  return [...new Set(mapped.filter(Boolean))];
};
const mergeUnique = (...lists) => [...new Set(lists.flat().filter(Boolean))];
const normalizeGenresList = (values = []) => normalizeAndDedupe(values, normalizeGenre);
const normalizeTypesList = (values = []) => normalizeAndDedupe(values, normalizeType);
const normalizeAndDedupe = (values = [], normalizer = (v) => v) => {
  const arr = Array.isArray(values) ? values : [];
  const deduped = [];
  const seen = new Set();
  arr.forEach((value) => {
    const normalized = normalizer(value);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(normalized);
  });
  return deduped;
};
const normalizeSearchTools = (tools = []) => {
  const incoming = Array.isArray(tools) ? tools : [];
  const normalized = incoming
    .map((tool, idx) => {
      if (typeof tool === 'string') {
        const url = normalizeToolUrl(tool);
        if (!url) return null;
        return { id: makeCustomToolId(url) || `custom:${idx}`, url, enabled: true, builtIn: false };
      }
      const url = normalizeToolUrl(tool?.url || tool?.domain || '');
      if (!url) return null;
      const builtInMatch = DEFAULT_SEARCH_TOOLS.find((d) => d.url === url);
      return {
        id: builtInMatch?.id || tool?.id || makeCustomToolId(url) || `custom:${idx}`,
        url,
        enabled: tool?.enabled !== false,
        builtIn: Boolean(builtInMatch || tool?.builtIn),
      };
    })
    .filter(Boolean);
  const deduped = [];
  const seen = new Set();
  normalized.forEach((tool) => {
    const key = `${tool.builtIn ? 'b' : 'c'}:${tool.url}`;
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(tool);
  });
  return deduped;
};
const normalizeTunePageSearchToolsDisabled = (value = {}) => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value).reduce((acc, [url, disabled]) => {
    const normalizedUrl = normalizeToolUrl(url);
    if (!normalizedUrl) return acc;
    acc[normalizedUrl] = Boolean(disabled);
    return acc;
  }, {});
};
const buildSearchToolsFromLegacyPrefs = (data = {}) => {
  const builtIns = DEFAULT_SEARCH_TOOLS.map((tool) => {
    const legacyKeyMap = {
      folkfriend: 'showFolkfriend',
      tunepal: 'showTunepal',
      tunearch: 'showTunearch',
      bluegrasslyrics: 'showBluegrasslyrics',
      thesession: 'showThesession',
    };
    const legacyKey = legacyKeyMap[tool.id];
    const enabled = legacyKey ? data?.[legacyKey] !== false : true;
    return { ...tool, enabled };
  });
  const customTools = Array.isArray(data?.customSearchTools)
    ? data.customSearchTools.map((tool) =>
      typeof tool === 'string'
        ? { id: makeCustomToolId(tool), url: normalizeToolUrl(tool), enabled: true, builtIn: false }
        : {
          id: tool.id || makeCustomToolId(tool?.url || ''),
          url: normalizeToolUrl(tool?.url || ''),
          enabled: tool?.enabled !== false,
          builtIn: false
        }
    ).filter((tool) => tool.url)
    : [];
  return normalizeSearchTools([...builtIns, ...customTools]);
};



useEffect(() => {
  const fetchPreferences = async () => {
    if (!user) return;
    const docRef = doc(db, 'users', user.uid, 'preferences', 'settings');
    const snap = await getDoc(docRef);

    let data = {};
    if (snap.exists()) {
      data = snap.data();
      const loadedDefaultGenreByMode =
        data.defaultGenreByMode && typeof data.defaultGenreByMode === 'object'
          ? data.defaultGenreByMode
          : {
              tunes: data.defaultGenre || getModeLabels('tunes').defaultGenre,
              notes: getModeLabels('notes').defaultGenre
            };
      const normalizedDefaultGenreByMode = {
        tunes: loadedDefaultGenreByMode.tunes || getModeLabels('tunes').defaultGenre,
        notes: normalizeNotesCategoryValue(loadedDefaultGenreByMode.notes) || getModeLabels('notes').defaultGenre
      };
      const loadedDefaultTypeByMode =
        data.defaultTypeByMode && typeof data.defaultTypeByMode === 'object'
          ? data.defaultTypeByMode
          : {
              tunes: data.defaultType || getModeLabels('tunes').defaultType,
              notes: getModeLabels('notes').defaultType
            };

      setDefaultGenreByMode(normalizedDefaultGenreByMode);
      setDefaultTypeByMode({
        tunes: loadedDefaultTypeByMode.tunes || getModeLabels('tunes').defaultType,
        notes: loadedDefaultTypeByMode.notes || getModeLabels('notes').defaultType
      });

      const initialMode = resolveDefaultMode(data.appMode || appMode);
      setDefaultGenre((normalizedDefaultGenreByMode[initialMode] || getModeLabels(initialMode).defaultGenre));
      setDefaultType((loadedDefaultTypeByMode[initialMode] || getModeLabels(initialMode).defaultType));
      if (data.defaultKey) setDefaultKey(data.defaultKey);
      if (data.defaultStatus) setDefaultStatus(data.defaultStatus);
      if (data.theme) setTheme(data.theme);
      if (data.enableAutoSuggest) setEnableAutoSuggest(data.enableAutoSuggest);
      if (data.showAbcInSets !== undefined) setShowAbcInSets(data.showAbcInSets);
      if (data.showLyricsInSets !== undefined) setShowLyricsInSets(data.showLyricsInSets);
      if (data.showAbcInSearch !== undefined) setShowAbcInSearch(data.showAbcInSearch);
      if (data.showLyricsInSearch !== undefined) setShowLyricsInSearch(data.showLyricsInSearch);
      if (data.showTuneCreatedDateInSearch !== undefined) setShowTuneCreatedDateInSearch(data.showTuneCreatedDateInSearch);
      if (data.showNotePreview !== undefined) setShowNotePreview(data.showNotePreview);
      if (data.showSetDateInSets !== undefined) setShowSetDateInSets(data.showSetDateInSets);
      if (data.showSessionDateInSessions !== undefined) setShowSessionDateInSessions(data.showSessionDateInSessions);
      const legacyTagPreviewPref = data.showTagPreview !== undefined ? data.showTagPreview : true;
      const searchTagPreviewPref =
        data.showTagPreviewInSearch !== undefined ? data.showTagPreviewInSearch : legacyTagPreviewPref;
      const setTagPreviewPref =
        data.showTagPreviewInSets !== undefined ? data.showTagPreviewInSets : legacyTagPreviewPref;
      const sessionTagPreviewPref =
        data.showTagPreviewInSessions !== undefined
          ? data.showTagPreviewInSessions
          : legacyTagPreviewPref;
      setShowTagPreviewInSearch(searchTagPreviewPref);
      setShowTagPreviewInSets(setTagPreviewPref);
      setShowTagPreviewInSessions(sessionTagPreviewPref);
      if (data.showTagPreviewInSearch === undefined && data.showTagPreview !== undefined) {
        updatePreference('showTagPreviewInSearch', searchTagPreviewPref);
      }
      if (data.showTagPreviewInSets === undefined && data.showTagPreview !== undefined) {
        updatePreference('showTagPreviewInSets', setTagPreviewPref);
      }
      if (data.showTagPreviewInSessions === undefined && data.showTagPreview !== undefined) {
        updatePreference('showTagPreviewInSessions', sessionTagPreviewPref);
      }
      if (data.showNoteCreatedDateInSearch !== undefined) setShowNoteCreatedDateInSearch(data.showNoteCreatedDateInSearch);
      const loadedSearchTools = Array.isArray(data.searchTools)
        ? normalizeSearchTools(data.searchTools)
        : buildSearchToolsFromLegacyPrefs(data);
      setSearchTools(loadedSearchTools);
      if (!Array.isArray(data.searchTools)) {
        updatePreference('searchTools', loadedSearchTools);
      }
      const loadedTunePageSearchToolsDisabled = normalizeTunePageSearchToolsDisabled(data.tunePageSearchToolsDisabled);
      setTunePageSearchToolsDisabled(loadedTunePageSearchToolsDisabled);
      if (
        data.tunePageSearchToolsDisabled !== undefined &&
        JSON.stringify(loadedTunePageSearchToolsDisabled) !== JSON.stringify(data.tunePageSearchToolsDisabled)
      ) {
        updatePreference('tunePageSearchToolsDisabled', loadedTunePageSearchToolsDisabled);
      }
      if (data.addTuneFieldsByMode && typeof data.addTuneFieldsByMode === 'object') {
        setAddTuneFieldsByMode({
          tunes: data.addTuneFieldsByMode.tunes || {},
          notes: data.addTuneFieldsByMode.notes || {}
        });
      } else if (data.addTuneFields && typeof data.addTuneFields === 'object') {
        const migrated = { tunes: data.addTuneFields, notes: data.addTuneFields };
        setAddTuneFieldsByMode(migrated);
        updatePreference('addTuneFieldsByMode', migrated);
      }
      if (data.appMode) setAppMode(data.appMode);

      // Migrate flat genre/type arrays to per-mode objects
      if (Array.isArray(data.customGenres)) {
        const migrated = {
          tunes: normalizeGenresList(mergeUnique(getModeLabels('tunes').defaultGenres || [], data.customGenres)),
          notes: normalizeGenresList(mergeUnique(getModeLabels('notes').defaultGenres || []))
        };
        setCustomGenres(migrated);
        updatePreference('customGenres', migrated);
      } else if (data.customGenres && typeof data.customGenres === 'object') {
        const normalizedCustomGenresRaw = {
          ...data.customGenres,
          notes: normalizeNotesCategoryList(data.customGenres.notes)
        };
        const normalizedCustomGenres = {
          ...normalizedCustomGenresRaw,
          tunes: normalizeGenresList(mergeUnique(getModeLabels('tunes').defaultGenres || [], normalizedCustomGenresRaw.tunes || [])),
          notes: normalizeGenresList(mergeUnique(getModeLabels('notes').defaultGenres || [], normalizedCustomGenresRaw.notes || []))
        };
        setCustomGenres(normalizedCustomGenres);
        if (JSON.stringify(normalizedCustomGenres) !== JSON.stringify(data.customGenres)) {
          updatePreference('customGenres', normalizedCustomGenres);
        }
      } else {
        const fresh = {
          tunes: normalizeGenresList(mergeUnique(getModeLabels('tunes').defaultGenres || [], baseGenres)),
          notes: normalizeGenresList(mergeUnique(getModeLabels('notes').defaultGenres || []))
        };
        setCustomGenres(fresh);
      }
      if (JSON.stringify(normalizedDefaultGenreByMode) !== JSON.stringify(loadedDefaultGenreByMode)) {
        updatePreference('defaultGenreByMode', normalizedDefaultGenreByMode);
        updatePreference('defaultGenre', normalizedDefaultGenreByMode.tunes || getModeLabels('tunes').defaultGenre);
      }

      if (Array.isArray(data.customTypes)) {
        const migrated = {
          tunes: normalizeTypesList(mergeUnique(getModeLabels('tunes').defaultTypes || [], data.customTypes)),
          notes: normalizeTypesList(mergeUnique(getModeLabels('notes').defaultTypes || []))
        };
        setCustomTypes(migrated);
        updatePreference('customTypes', migrated);
      } else if (data.customTypes && typeof data.customTypes === 'object') {
        const normalizedCustomTypes = {
          ...data.customTypes,
          tunes: normalizeTypesList(mergeUnique(getModeLabels('tunes').defaultTypes || [], data.customTypes.tunes || [])),
          notes: normalizeTypesList(mergeUnique(getModeLabels('notes').defaultTypes || [], data.customTypes.notes || []))
        };
        setCustomTypes(normalizedCustomTypes);
        if (JSON.stringify(normalizedCustomTypes) !== JSON.stringify(data.customTypes)) {
          updatePreference('customTypes', normalizedCustomTypes);
        }
      } else {
        const fresh = {
          tunes: normalizeTypesList(mergeUnique(getModeLabels('tunes').defaultTypes || [], baseTypes)),
          notes: normalizeTypesList(mergeUnique(getModeLabels('notes').defaultTypes || []))
        };
        setCustomTypes(fresh);
      }

      setCustomTags(
        normalizeAndDedupe(
          Array.isArray(data.customTags ?? data.customRatingLabels) ? (data.customTags ?? data.customRatingLabels) : baseTags,
          normalizeTag
        )
      );
    } else {
      const freshGenres = {
        tunes: normalizeGenresList(mergeUnique(getModeLabels('tunes').defaultGenres || [], baseGenres)),
        notes: normalizeGenresList(mergeUnique(getModeLabels('notes').defaultGenres || []))
      };
      const freshTypes = {
        tunes: normalizeTypesList(mergeUnique(getModeLabels('tunes').defaultTypes || [], baseTypes)),
        notes: normalizeTypesList(mergeUnique(getModeLabels('notes').defaultTypes || []))
      };
      data = {
        customGenres: freshGenres,
        customTypes: freshTypes,
        customTags: baseTags,
        defaultGenreByMode: { tunes: getModeLabels('tunes').defaultGenre, notes: getModeLabels('notes').defaultGenre },
        defaultTypeByMode: { tunes: getModeLabels('tunes').defaultType, notes: getModeLabels('notes').defaultType },
        addTuneFieldsByMode: { tunes: {}, notes: {} },
        enableAutoSuggest: 'yes',
        showAbcInSets: true,
        showLyricsInSets: true,
        showAbcInSearch: true,
        showLyricsInSearch: false,
        showTuneCreatedDateInSearch: true,
        showTagPreviewInSearch: true,
        showTagPreviewInSets: true,
        showTagPreviewInSessions: true,
        showSetDateInSets: true,
        showSessionDateInSessions: true,
        searchTools: DEFAULT_SEARCH_TOOLS,
        tunePageSearchToolsDisabled: {},
      };
      setDefaultGenreByMode({
        tunes: data.defaultGenreByMode.tunes,
        notes: normalizeNotesCategoryValue(data.defaultGenreByMode.notes) || getModeLabels('notes').defaultGenre
      });
      setDefaultTypeByMode(data.defaultTypeByMode);
      setAddTuneFieldsByMode(data.addTuneFieldsByMode);
      setDefaultGenre(data.defaultGenreByMode.tunes);
      setDefaultType(data.defaultTypeByMode.tunes);
      setCustomGenres(freshGenres);
      setCustomTypes(freshTypes);
      setCustomTags(normalizeAndDedupe(baseTags, normalizeTag));
      setEnableAutoSuggest('yes');
      setShowAbcInSets(true);
      setShowLyricsInSets(true);
      setShowAbcInSearch(true);
      setShowLyricsInSearch(false);
      setShowTuneCreatedDateInSearch(true);
      setShowTagPreviewInSearch(true);
      setShowTagPreviewInSets(true);
      setShowTagPreviewInSessions(true);
      setShowSetDateInSets(true);
      setShowSessionDateInSessions(true);
      setSearchTools(DEFAULT_SEARCH_TOOLS);
      setTunePageSearchToolsDisabled({});
    }

    if (navigator.onLine && user) {
      try {
        const tuneSnap = await getDocs(collection(db, 'users', user.uid, 'tunes'));
        const legacyNotes = tuneSnap.docs.filter((d) => {
          const item = d.data() || {};
          return (item.mode || 'tunes') === 'notes' && normalizeNotesCategoryValue(item.genre) !== item.genre;
        });
        for (const noteDoc of legacyNotes) {
          await updateDoc(doc(db, 'users', user.uid, 'tunes', noteDoc.id), { genre: NOTES_DEFAULT_CATEGORY });
        }
      } catch (err) {
        console.warn('Failed notes category migration:', err);
      }
    }

    await dexie.preferences.put({ id: 'settings', ...data }); // ✅ fixes 'data' is not defined
    setLoading(false);
  };

  fetchPreferences();
}, [user]);

useEffect(() => {
  const labelsForMode = getModeLabels(settingsMode);
  setDefaultGenre(defaultGenreByMode[settingsMode] || labelsForMode.defaultGenre);
  setDefaultType(defaultTypeByMode[settingsMode] || labelsForMode.defaultType);
}, [settingsMode, defaultGenreByMode, defaultTypeByMode]);

useEffect(() => {
  if (appMode !== 'all') {
    setAllModeSettingsMode(resolveDefaultMode(appMode));
  }
}, [appMode]);

// Keep Settings in sync with nav mode changes while this page is open
useEffect(() => {
  if (!liveAppMode) return;
  setAppMode(prev => (prev === liveAppMode ? prev : liveAppMode));
}, [liveAppMode]);



useEffect(() => {
  if (!user) return;

  const fetchUsedValues = async () => {
    const tunesRef = collection(db, 'users', user.uid, 'tunes');
    const setsRef = collection(db, 'users', user.uid, 'sets');
    const sessionsRef = collection(db, 'users', user.uid, 'sessions');
    const [snapshot, setsSnapshot, sessionsSnapshot] = await Promise.all([
      getDocs(tunesRef),
      getDocs(setsRef),
      getDocs(sessionsRef),
    ]);
    await dexie.tunes.bulkPut(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    const genres = new Set();
    const types = new Set();
    const tags = new Set();
    const tagsAnyMode = new Set();
    const usedNormalizedKeys = new Set();

    snapshot.docs.forEach(doc => {
      const d = doc.data();
      if (Array.isArray(d.customTags)) {
        d.customTags.forEach(tag => tagsAnyMode.add(normalizeTag(tag)));
      }
      const itemMode = d.mode || 'tunes';
      if (itemMode !== settingsMode) return;
      if (d.genre) genres.add(d.genre);
      if (d.type) types.add(d.type);
      if ((d.mode || 'tunes') === 'tunes' && d.key) {
        const normalized = normalizeKey(d.key);
        if (normalized) usedNormalizedKeys.add(normalized);
      }
      if (Array.isArray(d.customTags)) {
        d.customTags.forEach(tag => tags.add(normalizeTag(tag)));
      }
    });

    setsSnapshot.docs.forEach((docSnap) => {
      const d = docSnap.data() || {};
      const itemMode = d.mode || 'tunes';
      if (Array.isArray(d.customTags)) {
        d.customTags.forEach((tag) => tagsAnyMode.add(normalizeTag(tag)));
        if (itemMode === settingsMode) {
          d.customTags.forEach((tag) => tags.add(normalizeTag(tag)));
        }
      }
    });

    sessionsSnapshot.docs.forEach((docSnap) => {
      const d = docSnap.data() || {};
      const itemMode = d.mode || 'tunes';
      if (Array.isArray(d.customTags)) {
        d.customTags.forEach((tag) => tagsAnyMode.add(normalizeTag(tag)));
        if (itemMode === settingsMode) {
          d.customTags.forEach((tag) => tags.add(normalizeTag(tag)));
        }
      }
    });


    setUsedGenres(normalizeAndDedupe([...genres], normalizeGenre));
    setUsedTypes(normalizeAndDedupe([...types], normalizeType));
    setUsedTags(normalizeAndDedupe([...tags], normalizeTag));
    setUsedTagsAnyMode(normalizeAndDedupe([...tagsAnyMode], normalizeTag));
    const roots = ['C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'E#', 'Fb', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B', 'Cb'];
    const canonical = roots.flatMap((root) => [
      `${root} major`,
      `${root} minor`,
      `${root} dorian`,
      `${root} mixolydian`
    ]);
    setKeySuggestions([...new Set([...canonical, ...usedNormalizedKeys])].sort((a, b) => a.localeCompare(b)));

    // 🔄 Normalize and dedupe all tags before saving
    const existingTags = Array.isArray(userPrefs?.prefs?.customTags)
      ? userPrefs.prefs.customTags
      : customTags;
    const uniqueTags = normalizeAndDedupe([...existingTags, ...tagsAnyMode], normalizeTag);

    setCustomTags(uniqueTags);
    const normalizedExisting = normalizeAndDedupe(existingTags, normalizeTag);
    if (JSON.stringify(uniqueTags) !== JSON.stringify(normalizedExisting)) {
      updatePreference('customTags', uniqueTags);
    }


  };

  fetchUsedValues();
}, [user, settingsMode]);


useEffect(() => {
  if (theme) {
    const themes = ['theme-light', 'theme-dark', 'theme-sepia', 'theme-blueprint'];
    themes.forEach(t => document.body.classList.remove(t));
    document.body.classList.add(`theme-${theme}`);
  }
}, [theme]);


  const addGenre = () => {
    const trimmed = normalizeGenre(newGenreInput);
    const targetMode = appMode === 'all' ? allModeSettingsMode : appMode;
    const modeList = customGenres[targetMode] || [];
    if (trimmed && !modeList.some((g) => normalizeGenre(g) === trimmed)) {
      const updated = {
        ...customGenres,
        [targetMode]: normalizeAndDedupe([...modeList, trimmed], normalizeGenre)
      };
      setCustomGenres(updated);
      setNewGenreInput('');
      updatePreference('customGenres', updated);
    }
  };

  const removeGenre = (genre) => {
    const targetMode = appMode === 'all' ? allModeSettingsMode : appMode;
    const modeList = customGenres[targetMode] || [];
    const genreKey = normalizeGenre(genre);
    const updated = { ...customGenres, [targetMode]: modeList.filter((g) => normalizeGenre(g) !== genreKey) };
    setCustomGenres(updated);
    if (normalizeGenre(defaultGenreByMode[targetMode] || '') === genreKey) {
      const fallback = getModeLabels(targetMode).defaultGenre;
      const updatedDefaults = { ...defaultGenreByMode, [targetMode]: fallback };
      setDefaultGenreByMode(updatedDefaults);
      if (settingsMode === targetMode) setDefaultGenre(fallback);
      updatePreference('defaultGenreByMode', updatedDefaults);
      updatePreference('defaultGenre', updatedDefaults.tunes || fallback);
    }
    updatePreference('customGenres', updated);
  };

  const addType = () => {
    const trimmed = normalizeType(newTypeInput);
    const targetMode = appMode === 'all' ? allModeSettingsMode : appMode;
    const modeList = customTypes[targetMode] || [];
    if (trimmed && !modeList.some((t) => normalizeType(t) === trimmed)) {
      const updated = {
        ...customTypes,
        [targetMode]: normalizeAndDedupe([...modeList, trimmed], normalizeType)
      };
      setCustomTypes(updated);
      setNewTypeInput('');
      updatePreference('customTypes', updated);
    }
  };

const addTag = () => {
    const trimmed = normalizeTag(newTagInput);
    if (trimmed && !customTags.includes(trimmed)) {
      const updated = [...customTags, trimmed];

    setCustomTags(updated);
    setNewTagInput('');
    updatePreference('customTags', updated);
  }
};

const removeTag = (tag) => {
  const updated = customTags.filter(t => t !== tag);
  setCustomTags(updated);
  updatePreference('customTags', updated);
};

  const removeType = (type) => {
    const targetMode = appMode === 'all' ? allModeSettingsMode : appMode;
    const modeList = customTypes[targetMode] || [];
    const typeKey = normalizeType(type);
    const updated = { ...customTypes, [targetMode]: modeList.filter((t) => normalizeType(t) !== typeKey) };
    setCustomTypes(updated);
    if (normalizeType(defaultTypeByMode[targetMode] || '') === typeKey) {
      const fallback = getModeLabels(targetMode).defaultType;
      const updatedDefaults = { ...defaultTypeByMode, [targetMode]: fallback };
      setDefaultTypeByMode(updatedDefaults);
      if (settingsMode === targetMode) setDefaultType(fallback);
      updatePreference('defaultTypeByMode', updatedDefaults);
      updatePreference('defaultType', updatedDefaults.tunes || '');
    }
    updatePreference('customTypes', updated);
  };

  if (loading) return <p>Loading preferences...</p>;

  const modeLabels = getModeLabels(settingsMode);
  const currentGenres = normalizeGenresList(customGenres[settingsMode] || []);
  const currentTypes = normalizeTypesList(customTypes[settingsMode] || []);
  const visibleGenres = normalizeGenresList([...currentGenres, ...usedGenres]);
  const visibleTypes = normalizeTypesList([...currentTypes]);
  const currentAddFields = addTuneFieldsByMode[settingsMode] || {};
  const alwaysVisibleAddFields = settingsMode === 'notes'
    ? [
      { key: 'title', label: 'Title' },
      { key: 'notes', label: 'Notes' },
      { key: 'genre', label: modeLabels.genreLabel || 'Category' },
      { key: 'type', label: modeLabels.typeLabel || 'Type' },
    ]
    : [
      { key: 'title', label: 'Title' },
      { key: 'genre', label: modeLabels.genreLabel || 'Genre' },
      { key: 'type', label: modeLabels.typeLabel || 'Type' },
      { key: 'quickNote', label: 'Quick Note' },
    ];
  const optionalAddFields = [
    { key: 'composer', label: 'Composer', tunesOnly: true },
    { key: 'key', label: 'Key', tunesOnly: true },
    { key: 'status', label: 'Status' },
    { key: 'rating', label: 'Rating' },
    { key: 'notes', label: 'Notes', notesOnly: false },
    { key: 'lyrics', label: 'Lyrics', tunesOnly: true },
    { key: 'abc', label: 'ABC Notation', tunesOnly: true },
    { key: 'tags', label: 'Tags' },
    { key: 'attachments', label: 'Attachments' },
    { key: 'link1', label: 'Web Link' },
    { key: 'imageUrl', label: 'Image URL' },
    { key: 'recordings', label: 'Recordings' },
  ]
    .filter(field => !field.tunesOnly || settingsMode !== 'notes')
    .filter(field => !alwaysVisibleAddFields.some(always => always.key === field.key));
  const updateAddFieldsForMode = (updatedForMode) => {
    const updated = { ...addTuneFieldsByMode, [settingsMode]: updatedForMode };
    setAddTuneFieldsByMode(updated);
    updatePreference('addTuneFieldsByMode', updated);
  };
  const optionalAddFieldKeys = optionalAddFields.map(field => field.key);
  const tunePreviewColumns = [
    {
      key: 'search',
      heading: 'Search',
      options: [
        {
          key: 'showTuneCreatedDateInSearch',
          label: 'Date',
          enabled: showTuneCreatedDateInSearch,
          toggle: () => {
            const next = !showTuneCreatedDateInSearch;
            setShowTuneCreatedDateInSearch(next);
            updatePreference('showTuneCreatedDateInSearch', next);
          }
        },
        {
          key: 'showTagPreviewInSearch',
          label: 'Tags',
          enabled: showTagPreviewInSearch,
          toggle: () => {
            const next = !showTagPreviewInSearch;
            setShowTagPreviewInSearch(next);
            updatePreference('showTagPreviewInSearch', next);
          }
        },
        {
          key: 'showAbcInSearch',
          label: 'ABC',
          enabled: showAbcInSearch,
          toggle: () => {
            const next = !showAbcInSearch;
            setShowAbcInSearch(next);
            updatePreference('showAbcInSearch', next);
          }
        },
        {
          key: 'showLyricsInSearch',
          label: 'Lyrics',
          enabled: showLyricsInSearch,
          toggle: () => {
            const next = !showLyricsInSearch;
            setShowLyricsInSearch(next);
            updatePreference('showLyricsInSearch', next);
          }
        }
      ]
    },
    {
      key: 'sets',
      heading: 'Sets',
      options: [
        {
          key: 'showSetDateInSets',
          label: 'Date',
          enabled: showSetDateInSets,
          toggle: () => {
            const next = !showSetDateInSets;
            setShowSetDateInSets(next);
            updatePreference('showSetDateInSets', next);
          }
        },
        {
          key: 'showTagPreviewInSets',
          label: 'Tags',
          enabled: showTagPreviewInSets,
          toggle: () => {
            const next = !showTagPreviewInSets;
            setShowTagPreviewInSets(next);
            updatePreference('showTagPreviewInSets', next);
          }
        },
        {
          key: 'showAbcInSets',
          label: 'ABC',
          enabled: showAbcInSets,
          toggle: () => {
            const next = !showAbcInSets;
            setShowAbcInSets(next);
            updatePreference('showAbcInSets', next);
          }
        },
        {
          key: 'showLyricsInSets',
          label: 'Lyrics',
          enabled: showLyricsInSets,
          toggle: () => {
            const next = !showLyricsInSets;
            setShowLyricsInSets(next);
            updatePreference('showLyricsInSets', next);
          }
        }
      ]
    },
    {
      key: 'sessions',
      heading: 'Sessions',
      options: [
        {
          key: 'showSessionDateInSessions',
          label: 'Date',
          enabled: showSessionDateInSessions,
          toggle: () => {
            const next = !showSessionDateInSessions;
            setShowSessionDateInSessions(next);
            updatePreference('showSessionDateInSessions', next);
          }
        },
        {
          key: 'showTagPreviewInSessions',
          label: 'Tags',
          enabled: showTagPreviewInSessions,
          toggle: () => {
            const next = !showTagPreviewInSessions;
            setShowTagPreviewInSessions(next);
            updatePreference('showTagPreviewInSessions', next);
          }
        }
      ]
    }
  ];
  const notePreviewColumns = [
    {
      key: 'search',
      heading: 'Search',
      options: [
        {
          key: 'showNoteCreatedDateInSearch',
          label: 'Date',
          enabled: showNoteCreatedDateInSearch,
          toggle: () => {
            const next = !showNoteCreatedDateInSearch;
            setShowNoteCreatedDateInSearch(next);
            updatePreference('showNoteCreatedDateInSearch', next);
          }
        },
        {
          key: 'showTagPreviewInSearch',
          label: 'Tags',
          enabled: showTagPreviewInSearch,
          toggle: () => {
            const next = !showTagPreviewInSearch;
            setShowTagPreviewInSearch(next);
            updatePreference('showTagPreviewInSearch', next);
          }
        },
        {
          key: 'showNotePreview',
          label: 'Preview',
          enabled: showNotePreview,
          toggle: () => {
            const next = !showNotePreview;
            setShowNotePreview(next);
            updatePreference('showNotePreview', next);
          }
        }
      ]
    },
    {
      key: 'folders',
      heading: 'Folders',
      options: [
        {
          key: 'showSetDateInSets',
          label: 'Date',
          enabled: showSetDateInSets,
          toggle: () => {
            const next = !showSetDateInSets;
            setShowSetDateInSets(next);
            updatePreference('showSetDateInSets', next);
          }
        },
        {
          key: 'showTagPreviewInSets',
          label: 'Tags',
          enabled: showTagPreviewInSets,
          toggle: () => {
            const next = !showTagPreviewInSets;
            setShowTagPreviewInSets(next);
            updatePreference('showTagPreviewInSets', next);
          }
        }
      ]
    },
    {
      key: 'sessions',
      heading: 'Sessions',
      options: [
        {
          key: 'showSessionDateInSessions',
          label: 'Date',
          enabled: showSessionDateInSessions,
          toggle: () => {
            const next = !showSessionDateInSessions;
            setShowSessionDateInSessions(next);
            updatePreference('showSessionDateInSessions', next);
          }
        },
        {
          key: 'showTagPreviewInSessions',
          label: 'Tags',
          enabled: showTagPreviewInSessions,
          toggle: () => {
            const next = !showTagPreviewInSessions;
            setShowTagPreviewInSessions(next);
            updatePreference('showTagPreviewInSessions', next);
          }
        }
      ]
    }
  ];
  const statusSelectOptions = (modeLabels.statusOptions || []).map((opt, i) => ({ value: i === 0 ? '-' : opt, label: opt }));
  const activePreviewColumns = settingsMode === 'tunes' ? tunePreviewColumns : notePreviewColumns;
  const themeSelectOptions = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'sepia', label: 'Sepia' },
    { value: 'blueprint', label: 'Blueprint' },
  ];
  const uiSizeSelectOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
  ];
  const usedGenreKeys = new Set(usedGenres.map((genre) => normalizeGenre(genre).toLowerCase()));
  const usedTypeKeys = new Set(usedTypes.map((type) => normalizeType(type).toLowerCase()));
  const unusedGenres = currentGenres.filter((genre) => !usedGenreKeys.has(normalizeGenre(genre).toLowerCase()));
  const unusedTypes = currentTypes.filter((type) => !usedTypeKeys.has(normalizeType(type).toLowerCase()));
  const usedTagsAnyModeSet = new Set((usedTagsAnyMode || []).map((tag) => normalizeTag(tag)));
  const unusedTags = customTags.filter((tag) => !usedTagsAnyModeSet.has(normalizeTag(tag)));
  const persistSearchTools = (updatedTools) => {
    setSearchTools(updatedTools);
    updatePreference('searchTools', updatedTools);
  };
  const persistTunePageSearchToolsDisabled = (nextMap) => {
    setTunePageSearchToolsDisabled(nextMap);
    updatePreference('tunePageSearchToolsDisabled', nextMap);
  };
  const existingSearchToolUrls = new Set(searchTools.map((tool) => normalizeToolUrl(tool.url)));
  const missingDefaultSearchTools = DEFAULT_SEARCH_TOOLS.filter(
    (tool) => !existingSearchToolUrls.has(normalizeToolUrl(tool.url))
  );
  const removeSearchTool = (tool) => {
    if (!tool) return;
    setSearchToolsConfirm({ type: 'delete', tool });
  };
  const restoreDefaultSearchToolsInOrder = () => {
    const byUrl = new Map(searchTools.map((tool) => [normalizeToolUrl(tool.url), tool]));
    const restoredBuiltIns = DEFAULT_SEARCH_TOOLS.map((tool) => {
      const existing = byUrl.get(normalizeToolUrl(tool.url));
      return existing
        ? { ...existing, builtIn: true }
        : { ...tool };
    });
    const customTools = searchTools.filter(
      (tool) => !DEFAULT_SEARCH_TOOLS.some((d) => normalizeToolUrl(d.url) === normalizeToolUrl(tool.url))
    );
    persistSearchTools([...restoredBuiltIns, ...customTools]);
  };
  const openSearchWithFilter = (kind, value) => {
    if (!value) return;
    const params = new URLSearchParams();
    params.set(kind, value);
    navigate(`/search?${params.toString()}`);
  };
  const buildTunePageVisibilityMap = (disabled) =>
    searchTools.reduce((acc, tool) => {
      const normalizedUrl = normalizeToolUrl(tool.url);
      if (normalizedUrl) acc[normalizedUrl] = Boolean(disabled);
      return acc;
    }, {});

  return (
    <div className="settings-page" style={{ padding: '20px', maxWidth: '600px', margin: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.5rem' }}>
        <button className="btn-subtle search-control-btn" onClick={onBack} style={{ fontSize: 'var(--ui-compact-font)', padding: '5px 11px' }}>← Back</button>
      </div>
    
      <h1 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
        Settings
        <FaQuestionCircle
          onClick={() => navigate('/help#customization')}
          style={{ fontSize: '0.65em', cursor: 'pointer', color: 'var(--text-link, #228be6)', opacity: 0.8 }}
          title="Help & Tutorials"
        />
      </h1>

      <div style={{ margin: '0 auto 16px' }}>
        <div className="settings-top-help-row">
          <button className="btn-subtle search-control-btn settings-top-help-btn" onClick={() => navigate('/help')} style={{ cursor: 'pointer', fontWeight: 700, letterSpacing: '0.01em', fontSize: 'var(--ui-compact-font)', padding: '6px 12px' }}>
            Help &amp; Tutorials
          </button>
          <button
            className="btn-subtle search-control-btn settings-top-hints-btn"
            onClick={async () => {
              const next = !hintsOn;
              if (userPrefs?.savePrefs && userPrefs?.prefs) {
                await userPrefs.savePrefs({ ...userPrefs.prefs, showFieldHints: next });
              } else {
                await updatePreference('showFieldHints', next);
              }
            }}
            style={{
              cursor: 'pointer',
              fontWeight: 700,
              letterSpacing: '0.01em',
              fontSize: 'var(--ui-compact-font)',
              padding: '6px 12px'
            }}
          >
            {hintsOn ? 'Field Hints: ON' : 'Field Hints: OFF'}
          </button>
        </div>

        <ActionConfirmModal
          open={Boolean(downloadAlertModal)}
          title={downloadAlertModal?.title || ''}
          message={downloadAlertModal?.message || ''}
          confirmLabel="OK"
          hideCancel
          onConfirm={() => setDownloadAlertModal(null)}
          onCancel={() => setDownloadAlertModal(null)}
        />

        <ActionConfirmModal
          open={showModeToggleConfirm}
          title={isSimpleMode ? 'Switch to Full Mode?' : 'Switch to Simple Mode?'}
          message={isSimpleMode
            ? 'Full Mode unlocks Sessions, Social, advanced search & filters, and program templates.'
            : 'Simple Mode hides Sessions, Social, advanced filters, and program templates. Your data is always preserved.'}
          confirmLabel={isSimpleMode ? 'Enable Full Mode' : 'Switch to Simple'}
          cancelLabel="Cancel"
          onConfirm={async () => { setShowModeToggleConfirm(false); await toggleComplexity(); }}
          onCancel={() => setShowModeToggleConfirm(false)}
        />

        <div className="settings-top-ui-row">
          <div className="settings-top-select-wrap">
            <AppSelect
              className="search-core-select settings-theme-select"
              value={theme}
              onChange={(value) => {
                setTheme(value);
                updatePreference('theme', value);
              }}
              options={themeSelectOptions}
              placeholder="—"
              selectedPrefix="Theme"
              active={Boolean(theme)}
              style={inputStyle}
            />
          </div>
          <div className="settings-top-select-wrap">
            <AppSelect
              className="search-core-select settings-ui-size-select"
              value={uiSize}
              onChange={(newSize) => {
                setUiSize(newSize);
                updatePreference('uiSize', newSize);
                localStorage.setItem('uiSize', newSize);
              }}
              options={uiSizeSelectOptions}
              placeholder="—"
              selectedPrefix="UI Size"
              active={Boolean(uiSize)}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {(
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
          <button
            className="btn-subtle search-control-btn"
            onClick={() => setShowModeToggleConfirm(true)}
            style={{ cursor: 'pointer', fontWeight: 700, fontSize: 'var(--ui-compact-font)', padding: '6px 16px' }}
          >
            {isSimpleMode ? 'Mode: Simple 🔒' : 'Mode: Full ✓'}
          </button>
        </div>
      )}

      <div className="settings-download-row" style={{ marginTop: '4px', marginBottom: '16px' }}>
        <button className="btn-subtle search-control-btn settings-download-btn" onClick={() => downloadAllToLocal({
          onSuccess: (msg) => setDownloadAlertModal({ title: 'Downloaded', message: msg }),
          onError: (msg) => setDownloadAlertModal({ title: 'Error', message: msg }),
        })}>
          ⬇️ Download All for Offline Use
        </button>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
        <div className="mode-switcher-pill">
          {[{ value: 'tunes', label: 'Tune Settings' }, { value: 'notes', label: 'Note Settings' }].map((opt) => {
            const activeValue = appMode === 'all' ? allModeSettingsMode : appMode;
            const isActive = activeValue === opt.value;
            return (
              <button
                key={opt.value}
                className={`mode-switcher-btn ${isActive ? 'mode-switcher-btn-active' : ''}`}
                onClick={async () => {
                  if (appMode === 'all') {
                    setAllModeSettingsMode(opt.value);
                    return;
                  }
                  if (appMode !== opt.value) {
                    setAppMode(opt.value);
                    setAllModeSettingsMode(opt.value);
                    await updatePreference('appMode', opt.value);
                  }
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ maxWidth: '240px', margin: '6px auto 0' }}>
        <AppSelect
          className="search-core-select"
          value={defaultGenre}
          onChange={(value) => {
            const targetMode = settingsMode;
            const updated = { ...defaultGenreByMode, [targetMode]: value };
            setDefaultGenre(value);
            setDefaultGenreByMode(updated);
            updatePreference('defaultGenreByMode', updated);
            updatePreference('defaultGenre', updated.tunes || value);
          }}
          options={visibleGenres.map((g) => ({ value: g, label: g }))}
          placeholder="—"
          selectedPrefix={`Default ${modeLabels.genreLabel}`}
          active={Boolean(defaultGenre)}
          style={inputStyle}
        />
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ maxWidth: '240px', margin: '6px auto 0' }}>
        <AppSelect
          className="search-core-select"
          value={defaultType}
          onChange={(value) => {
            const targetMode = settingsMode;
            const updated = { ...defaultTypeByMode, [targetMode]: value };
            setDefaultType(value);
            setDefaultTypeByMode(updated);
            updatePreference('defaultTypeByMode', updated);
            updatePreference('defaultType', updated.tunes || value);
          }}
          options={visibleTypes.map((t) => ({ value: t, label: t }))}
          placeholder="—"
          selectedPrefix={`Default ${modeLabels.typeLabel}`}
          active={Boolean(defaultType)}
          style={inputStyle}
        />
        </div>
      </div>

      {settingsMode === 'tunes' && (
      <div style={{ marginBottom: '20px' }}>
        <div style={{ maxWidth: '240px', margin: '6px auto 0' }}>
          <KeyInput
            value={defaultKey}
            onChange={(e) => {
              setDefaultKey(e.target.value);
              updatePreference('defaultKey', e.target.value);
            }}
            suggestions={keySuggestions}
            placeholder="e.g. D, Em"
            selectedPrefix="Default Key"
            inputStyle={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
      </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <div style={{ maxWidth: '240px', margin: '6px auto 0' }}>
        <AppSelect
          className="search-core-select"
          value={defaultStatus}
          onChange={(value) => {
            setDefaultStatus(value);
            updatePreference('defaultStatus', value);
          }}
          options={statusSelectOptions}
          placeholder="—"
          selectedPrefix="Default Status"
          active={Boolean(defaultStatus && defaultStatus !== '-')}
          style={inputStyle}
        />
        </div>
      </div>

<hr className="settings-divider" />

{isSimpleMode && (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '10px', padding: '10px 14px', marginBottom: '12px',
    borderRadius: '10px',
    background: 'color-mix(in srgb, var(--text-muted, #888) 8%, var(--bg-card, #fff) 92%)',
    border: '1px solid color-mix(in srgb, var(--text-muted, #888) 25%, var(--border-color, #ccc) 75%)',
    fontSize: '0.88rem',
  }}>
    <span style={{ opacity: 0.75 }}>🔒 Advanced settings — available in Full Mode</span>
    <button
      className="btn-subtle search-control-btn"
      style={{ whiteSpace: 'nowrap', fontSize: '0.82rem', padding: '5px 13px' }}
      onClick={() => setShowSettingsLockPopup(true)}
    >
      Unlock Full Mode
    </button>
  </div>
)}

{!isSimpleMode && <div>

<details open className="settings-tone-group settings-tone-group-1" style={{ marginBottom: '12px' }}>
<summary style={{ cursor: 'pointer' }}><h3 className="settings-section-heading">{getModeLabels(settingsMode).customizeTitle}</h3></summary>
<div style={{ marginTop: '8px' }}>
  <div className="settings-pill-section">
    <div className="settings-pill-subtitle">Always Visible</div>
    <div className="settings-always-text">
      {alwaysVisibleAddFields.map(field => field.label).join(' • ')}
    </div>
  </div>

  {settingsMode === 'tunes' && (
    <div className="settings-pill-section" style={{ marginTop: '8px' }}>
      <div className="settings-pill-subtitle">Autosuggest From Session</div>
      <div className="settings-pill-actions" style={{ marginBottom: 0 }}>
        <button
          type="button"
          className={`btn-subtle settings-field-pill ${enableAutoSuggest === 'yes' ? 'settings-field-pill-on' : 'settings-field-pill-off'}`}
          style={{ width: '90px' }}
          onClick={() => {
            if (enableAutoSuggest === 'yes') return;
            setEnableAutoSuggest('yes');
            updatePreference('enableAutoSuggest', 'yes');
          }}
        >
          Yes
        </button>
        <button
          type="button"
          className={`btn-subtle settings-field-pill ${enableAutoSuggest === 'no' ? 'settings-field-pill-on' : 'settings-field-pill-off'}`}
          style={{ width: '90px' }}
          onClick={() => {
            if (enableAutoSuggest === 'no') return;
            setEnableAutoSuggest('no');
            updatePreference('enableAutoSuggest', 'no');
          }}
        >
          No
        </button>
      </div>
    </div>
  )}

  <div className="settings-pill-section" style={{ marginTop: '10px' }}>
    <div className="settings-pill-subtitle">Optional Fields</div>
    <div className="settings-pill-actions">
      <button
        type="button"
        className="btn-subtle search-control-btn settings-pill-action-btn"
        onClick={() => {
          const updatedForMode = { ...currentAddFields };
          optionalAddFieldKeys.forEach((key) => { updatedForMode[key] = true; });
          updateAddFieldsForMode(updatedForMode);
        }}
      >
        Show All
      </button>
      <button
        type="button"
        className="btn-subtle search-control-btn settings-pill-action-btn"
        onClick={() => {
          const updatedForMode = { ...currentAddFields };
          optionalAddFieldKeys.forEach((key) => { updatedForMode[key] = false; });
          updateAddFieldsForMode(updatedForMode);
        }}
      >
        Hide All
      </button>
    </div>
    <div className="settings-pill-grid">
      {optionalAddFields.map(field => {
        const isOn = currentAddFields[field.key] !== false;
        return (
          <button
            key={field.key}
            type="button"
            className={`btn-subtle settings-field-pill ${isOn ? 'settings-field-pill-on' : 'settings-field-pill-off'}`}
            onClick={() => {
              const updatedForMode = {
                ...currentAddFields,
                [field.key]: !isOn
              };
              updateAddFieldsForMode(updatedForMode);
            }}
          >
            {field.label}
          </button>
        );
      })}
    </div>
  </div>
</div>
</details>

<hr className="settings-divider" />

<details open className="settings-tone-group settings-tone-group-2" style={{ marginBottom: '12px' }}>
<summary style={{ cursor: 'pointer' }}><h3 className="settings-section-heading">Preview Settings</h3></summary>
<div style={{ marginTop: '8px' }}>
  <div className="settings-pill-section">
    <div className={`settings-preview-columns ${activePreviewColumns.length === 3 ? 'settings-preview-columns-3' : 'settings-preview-columns-2'}`}>
      {activePreviewColumns.map((group) => (
        <div key={group.key} className="settings-preview-column">
          <div className="settings-preview-heading">{group.heading}</div>
          <div className="settings-preview-pill-list">
            {group.options.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`btn-subtle settings-field-pill ${opt.enabled ? 'settings-field-pill-on' : 'settings-field-pill-off'}`}
                onClick={opt.toggle}
                title={opt.enabled ? `${opt.label}: On` : `${opt.label}: Off`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>

</div>
</details>

{settingsMode === 'tunes' && (
<>
<hr className="settings-divider" />
<details open className="settings-tone-group settings-tone-group-3" style={{ marginBottom: '12px' }}>
<summary style={{ cursor: 'pointer' }}><h3 className="settings-section-heading">Search Tools</h3></summary>
<div style={{ marginTop: '8px' }}>
  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
    <div style={{
      display: 'inline-flex',
      borderRadius: '12px',
      border: '1px solid var(--card-border, #ccc)',
      overflow: 'hidden',
      backgroundColor: 'var(--input-bg)',
      padding: '2px'
    }}>
      {[
        { value: 'add', label: 'Add Page' },
        { value: 'tuneDetail', label: 'Tune Detail Page' }
      ].map((opt) => {
        const isActive = searchToolsSettingsTarget === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => setSearchToolsSettingsTarget(opt.value)}
            style={{
              padding: '5px 12px',
              fontSize: '0.78rem',
              fontWeight: isActive ? 700 : 500,
              backgroundColor: isActive ? 'var(--bg-card)' : 'transparent',
              color: 'var(--text-primary)',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.15s, color 0.15s',
              borderRadius: '9px',
              boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.14)' : 'none'
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
  {searchToolsSettingsTarget === 'add' && (
  <div className="settings-pill-section">
    <div className="settings-pill-subtitle">Add Page Visibility</div>
    <div className="settings-pill-actions">
      <button
        type="button"
        className="btn-subtle search-control-btn settings-pill-action-btn"
        onClick={() => persistSearchTools(searchTools.map((tool) => ({ ...tool, enabled: true })))}
      >
        Show All
      </button>
      <button
        type="button"
        className="btn-subtle search-control-btn settings-pill-action-btn"
        onClick={() => persistSearchTools(searchTools.map((tool) => ({ ...tool, enabled: false })))}
      >
        Hide All
      </button>
      {missingDefaultSearchTools.length > 0 && (
        <button
          type="button"
          className="btn-subtle search-control-btn settings-pill-action-btn"
          onClick={() => {
            setSearchToolsConfirm({ type: 'restore' });
          }}
        >
          Restore Defaults
        </button>
      )}
    </div>
    <div className="settings-removable-list">
      {searchTools.map((tool) => (
        <div key={tool.id} className="settings-removable-row">
          <button
            type="button"
            className={`btn-subtle settings-field-pill ${tool.enabled ? 'settings-field-pill-on' : 'settings-field-pill-off'}`}
            onClick={() => persistSearchTools(searchTools.map((item) => (
              item.id === tool.id ? { ...item, enabled: !item.enabled } : item
            )))}
            title={tool.enabled ? `${tool.url}: On` : `${tool.url}: Off`}
          >
            {tool.url}
          </button>
          <button
            type="button"
            className="btn-subtle settings-removable-x"
            onClick={() => removeSearchTool(tool)}
            title="Delete tool"
            aria-label={`Delete ${tool.url}`}
          >
            X
          </button>
        </div>
      ))}
      {searchTools.length === 0 && (
        <div className="settings-always-text">No search tools configured.</div>
      )}
    </div>
  </div>
  )}
  {searchToolsSettingsTarget === 'tuneDetail' && (
  <div className="settings-pill-section">
    <div className="settings-pill-subtitle">Tune Detail Visibility</div>
    <div className="settings-pill-actions">
      <button
        type="button"
        className="btn-subtle search-control-btn settings-pill-action-btn"
        onClick={() => persistTunePageSearchToolsDisabled(buildTunePageVisibilityMap(false))}
      >
        Show All
      </button>
      <button
        type="button"
        className="btn-subtle search-control-btn settings-pill-action-btn"
        onClick={() => persistTunePageSearchToolsDisabled(buildTunePageVisibilityMap(true))}
      >
        Hide All
      </button>
    </div>
    <div className="settings-removable-list">
      {searchTools.map((tool) => {
        const normalizedUrl = normalizeToolUrl(tool.url);
        const hiddenOnTunePage = Boolean(tunePageSearchToolsDisabled[normalizedUrl]);
        return (
          <div key={`tune-page-${tool.id}`} className="settings-removable-row">
            <button
              type="button"
              className={`btn-subtle settings-field-pill ${hiddenOnTunePage ? 'settings-field-pill-off' : 'settings-field-pill-on'}`}
              onClick={() =>
                persistTunePageSearchToolsDisabled({
                  ...tunePageSearchToolsDisabled,
                  [normalizedUrl]: !hiddenOnTunePage,
                })
              }
              title={hiddenOnTunePage ? `${tool.url}: Hidden on Tune page` : `${tool.url}: Shown on Tune page`}
            >
              {tool.url}
            </button>
          </div>
        );
      })}
      {searchTools.length === 0 && (
        <div className="settings-always-text">No search tools configured.</div>
      )}
    </div>
  </div>
  )}

{searchToolsSettingsTarget === 'add' && (
<div className="settings-search-tool-addrow">
  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <input
      className="search-input"
      style={{ ...inputStyle, width: '200px', gridColumn: 'auto' }}
      placeholder="Add a new site: e.g. google.com"
      value={newSearchToolUrl}
      onChange={(e) => setNewSearchToolUrl(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          const trimmed = normalizeToolUrl(newSearchToolUrl);
          if (trimmed && !searchTools.some((tool) => normalizeToolUrl(tool.url) === trimmed)) {
            const updated = [...searchTools, { id: makeCustomToolId(trimmed), url: trimmed, enabled: true, builtIn: false }];
            persistSearchTools(updated);
            setNewSearchToolUrl('');
          }
        }
      }}
    />
    <button
      className="btn-subtle search-control-btn"
      style={{ fontSize: 'var(--ui-compact-font)', padding: '4px 10px' }}
      onClick={() => {
        const trimmed = normalizeToolUrl(newSearchToolUrl);
        if (trimmed && !searchTools.some((tool) => normalizeToolUrl(tool.url) === trimmed)) {
          const updated = [...searchTools, { id: makeCustomToolId(trimmed), url: trimmed, enabled: true, builtIn: false }];
          persistSearchTools(updated);
          setNewSearchToolUrl('');
        }
      }}
    >
      Add
    </button>
  </label>
</div>
)}
</div>
</details>
</>
)}

<ActionConfirmModal
  open={Boolean(searchToolsConfirm)}
  title={searchToolsConfirm?.type === 'restore' ? 'Restore Defaults?' : 'Delete Search Tool?'}
  message={
    searchToolsConfirm?.type === 'restore'
      ? 'Re-add any missing default search tools?'
      : (searchToolsConfirm?.tool?.builtIn
        ? `Delete built-in tool "${searchToolsConfirm?.tool?.url}"? You can restore it later with Restore Defaults.`
        : `Delete custom tool "${searchToolsConfirm?.tool?.url}"?`)
  }
  confirmLabel={searchToolsConfirm?.type === 'restore' ? 'Restore' : 'Delete'}
  cancelLabel="Cancel"
  danger
  onConfirm={() => {
    if (searchToolsConfirm?.type === 'restore') {
      restoreDefaultSearchToolsInOrder();
    } else if (searchToolsConfirm?.type === 'delete' && searchToolsConfirm?.tool) {
      const nextTools = searchTools.filter((item) => item.id !== searchToolsConfirm.tool.id);
      persistSearchTools(nextTools);
      const nextDisabled = { ...tunePageSearchToolsDisabled };
      delete nextDisabled[normalizeToolUrl(searchToolsConfirm.tool.url)];
      persistTunePageSearchToolsDisabled(nextDisabled);
    }
    setSearchToolsConfirm(null);
  }}
  onCancel={() => setSearchToolsConfirm(null)}
/>

<hr className="settings-divider" />

      <details className="settings-taxonomy-section settings-taxonomy-tone-4" open>
      <summary className="settings-taxonomy-title">
        <h3 className="settings-section-heading">
          {getModeLabels(settingsMode).genreLabelPlural === 'Categories' ? 'AllMyCategories' : 'AllMyGenres'}
        </h3>
      </summary>

      {usedGenres.length > 0 && (
        <div className="settings-taxonomy-details">
          <div className="settings-taxonomy-summary">
            {getModeLabels(settingsMode).genreLabelPlural} in use ({usedGenres.length})
          </div>
          <div className="settings-taxonomy-chip-wrap">
            {usedGenres.map((genre) => (
              <button
                key={genre}
                type="button"
                className="btn-subtle settings-taxonomy-chip settings-taxonomy-chip-clickable"
                onClick={() => openSearchWithFilter('genre', genre)}
                title={`Filter Search by ${genre}`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>
      )}

      {unusedGenres.length > 0 && (
        <div className="settings-taxonomy-details">
          <div className="settings-taxonomy-summary">
            Unused {getModeLabels(settingsMode).genreLabelPlural.toLowerCase()} ({unusedGenres.length})
          </div>
          <div className="settings-taxonomy-chip-wrap">
            {unusedGenres.map((genre, i) => (
              <div key={`${genre}-${i}`} className="settings-taxonomy-chip settings-taxonomy-chip-removable">
                <span>{genre}</span>
                <button
                  onClick={() => removeGenre(genre)}
                  className="settings-taxonomy-chip-remove"
                  aria-label={`Remove ${genre}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="settings-taxonomy-addrow">
        <input
          className="search-input"
          style={{ ...inputStyle, flex: 1, minWidth: 0, gridColumn: 'auto' }}
          placeholder={`Add new ${getModeLabels(settingsMode).genreLabel.toLowerCase()}`}
          value={newGenreInput}
          onChange={(e) => setNewGenreInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addGenre()}
        />
        <button className="btn-subtle search-control-btn" onClick={addGenre}>Add</button>
      </div>
      </details>

      <details className="settings-taxonomy-section settings-taxonomy-tone-5" open>
      <summary className="settings-taxonomy-title">
        <h3 className="settings-section-heading">AllMy{getModeLabels(settingsMode).typeLabel === 'Type' ? 'Types' : 'Types'}</h3>
      </summary>

      {usedTypes.length > 0 && (
        <div className="settings-taxonomy-details">
          <div className="settings-taxonomy-summary">
            Types in use ({usedTypes.length})
          </div>
          <div className="settings-taxonomy-chip-wrap">
            {usedTypes.map((type) => (
              <button
                key={type}
                type="button"
                className="btn-subtle settings-taxonomy-chip settings-taxonomy-chip-clickable"
                onClick={() => openSearchWithFilter('type', type)}
                title={`Filter Search by ${type}`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      )}

      {unusedTypes.length > 0 && (
        <div className="settings-taxonomy-details">
          <div className="settings-taxonomy-summary">
            Unused types ({unusedTypes.length})
          </div>
          <div className="settings-taxonomy-chip-wrap">
            {unusedTypes.map((type, i) => (
              <div key={`${type}-${i}`} className="settings-taxonomy-chip settings-taxonomy-chip-removable">
                <span>{type}</span>
                <button
                  onClick={() => removeType(type)}
                  className="settings-taxonomy-chip-remove"
                  aria-label={`Remove ${type}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="settings-taxonomy-addrow">
        <input
          className="search-input"
          style={{ ...inputStyle, flex: 1, minWidth: 0, gridColumn: 'auto' }}
          placeholder="Add new type"
          value={newTypeInput}
          onChange={(e) => setNewTypeInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addType()}
        />
        <button className="btn-subtle search-control-btn" onClick={addType}>Add</button>
      </div>
      </details>

      <details className="settings-taxonomy-section settings-taxonomy-tone-6" open>
      <summary className="settings-taxonomy-title">
        <h3 className="settings-section-heading">AllMyTags</h3>
      </summary>

      {usedTags.length > 0 && (
        <div className="settings-taxonomy-details">
          <div className="settings-taxonomy-summary">
            Tags in use ({usedTags.length})
          </div>
          <div className="settings-taxonomy-chip-wrap">
            {usedTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className="btn-subtle settings-taxonomy-chip settings-taxonomy-chip-clickable"
                onClick={() => openSearchWithFilter('tag', tag)}
                title={`Filter Search by tag: ${tag}`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {unusedTags.length > 0 && (
        <div className="settings-taxonomy-details">
          <div className="settings-taxonomy-summary">
            Unused tags ({unusedTags.length})
          </div>
          <div className="settings-taxonomy-chip-wrap">
            {unusedTags.map((tag, i) => (
              <div key={`${tag}-${i}`} className="settings-taxonomy-chip settings-taxonomy-chip-removable">
                <span>{tag}</span>
                <button
                  onClick={() => removeTag(tag)}
                  className="settings-taxonomy-chip-remove"
                  aria-label={`Remove ${tag}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="settings-taxonomy-addrow">
        <input
          className="search-input"
          style={{ ...inputStyle, flex: 1, minWidth: 0, gridColumn: 'auto' }}
          placeholder="Add new tag"
          value={newTagInput}
          onChange={(e) => setNewTagInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTag()}
        />
        <button className="btn-subtle search-control-btn" onClick={addTag}>Add</button>
      </div>
      </details>





</div>}{/* end advanced settings wrapper */}

<hr className="settings-divider settings-divider-section-end" />

      <ActionConfirmModal
        open={showSettingsLockPopup}
        title="Advanced Settings"
        message="These settings let you customize the Add page, search tools, genres, types, and tags. Full Mode also unlocks Sessions, Social, and program templates."
        confirmLabel="Enable Full Mode"
        cancelLabel="Not yet"
        onConfirm={() => { setShowSettingsLockPopup(false); unlockFullMode(); }}
        onCancel={() => setShowSettingsLockPopup(false)}
      />
    </div>

  );

  
};


export default Settings;
